import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus,
  Filter,
  Search,
  List,
  Grid,
  Mail,
  Archive,
  Calendar,
  Download,
  Upload,
  ChevronDown,
  Phone,
  MessageSquare,
  FileText,
  MoreVertical, 
  ChevronRight,
  Trash2
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import EmptyState from '../components/EmptyState';
import CreateBookingModal from '../components/CreateBookingModal';
import BookingInfoModal from '../components/BookingInfoModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import PermissionGuard from '../components/PermissionGuard';
import { useBookings } from '../hooks/useBookings'; 
import { deleteBooking, getBooking, type Booking } from '../lib/bookings';
import { useBusiness } from '../contexts/BusinessContext';
import { useEventDrivenModalPersistence } from '../utils/modalPersistence';
import { triggerBookingAutomations } from '../lib/automationsV2';

const statusOptions = [
  { label: 'All Bookings', value: 'all' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
];

function Bookings() {
  const { selectedBusiness } = useBusiness();
  const location = useLocation();
  const [permissionLevel, setPermissionLevel] = useState<'manage' | 'view' | 'disabled'>('manage');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState<Set<string>>(new Set());
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isBookingInfoModalOpen, setIsBookingInfoModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const { bookings, loading, error, totalCount, refetch } = useBookings(selectedBusiness?.id);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [bookingsToDelete, setBookingsToDelete] = useState<string[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Event-driven modal persistence for CreateBookingModal
  const { hasModalData, clearModalData } = useEventDrivenModalPersistence('create_booking');
  
  // Check for saved modal data on page load and auto-open if found
  useEffect(() => {
    const checkSavedModalData = () => {
      const hasData = hasModalData();
      console.log('🔍 Checking for saved CreateBookingModal data on Bookings page load:', hasData);
      if (hasData) {
        console.log('📥 Found saved CreateBookingModal progress - reopening modal (Bookings.tsx)');
        setIsCreateBookingModalOpen(true);
      }
    };
    
    // Check immediately first
    checkSavedModalData();
    
    // Also check with multiple delays to handle different initialization timings
    const timer1 = setTimeout(checkSavedModalData, 100);
    const timer2 = setTimeout(checkSavedModalData, 500);
    const timer3 = setTimeout(checkSavedModalData, 1000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [hasModalData]);

  // Add debug logging
  useEffect(() => {
    console.log('Bookings page state:', {
      selectedBusiness,
      permissionLevel,
      bookingsCount: bookings.length,
      loading,
      error
    });
  }, [selectedBusiness, permissionLevel, bookings, loading, error]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setIsActionsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset error state when business changes
  useEffect(() => {
    if (!selectedBusiness?.id) {
      setSearchQuery('');
      setSelectedBookings(new Set());
    }
  }, [selectedBusiness?.id]);

  // Handle navigation from customer details modal with booking ID
  useEffect(() => {
    const handleBookingNavigation = async () => {
      const state = location.state as { selectedBookingId?: string } | null;
      if (state?.selectedBookingId && selectedBusiness?.id) {
        try {
          // Fetch the specific booking details
          const booking = await getBooking(state.selectedBookingId);
          setSelectedBooking(booking);
          setIsBookingInfoModalOpen(true);
          
          // Clear the navigation state to prevent reopening on refresh
          window.history.replaceState({}, document.title);
        } catch (err) {
          console.error('Error fetching booking from navigation:', err);
        }
      }
    };

    handleBookingNavigation();
  }, [location.state, selectedBusiness?.id]);

  const filteredBookings = bookings.filter((booking) => {
    if (
      selectedStatus.value !== 'all' &&
      booking.status !== selectedStatus.value
    ) {
      return false;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      // Customer information
      booking.customer?.first_name?.toLowerCase().includes(searchLower) ||
      booking.customer?.last_name?.toLowerCase().includes(searchLower) ||
      booking.customer?.email?.toLowerCase().includes(searchLower) ||
      booking.customer?.phone?.toLowerCase().includes(searchLower) ||
      // Booking number (convert to string first)
      booking.booking_number?.toString().toLowerCase().includes(searchLower) ||
      // Services and vehicle information
      (booking.services || []).some(
        (service) =>
          service.name.toLowerCase().includes(searchLower) ||
          service.vehicle_make?.toLowerCase().includes(searchLower) ||
          service.vehicle_model?.toLowerCase().includes(searchLower)
      )
    );
  });

  const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedBookings(new Set(bookings.map((booking) => booking.id)));
    } else {
      setSelectedBookings(new Set());
    }
  };

  const toggleBooking = (id: string) => {
    const newSelected = new Set(selectedBookings);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedBookings(newSelected);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-white';
      case 'scheduled':
        return 'text-white';
      case 'confirmed':
        return 'text-white';
      case 'in_progress':
        return 'text-white';
      case 'completed':
        return 'text-white';
      case 'cancelled':
        return 'text-red-700';
      default:
        return 'text-white';
    }
  };

  const getStatusBackgroundColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#a855f7'; // Purple
      case 'scheduled':
        return '#3b82f6'; // Blue
      case 'confirmed':
        return '#3b82f6'; // Blue
      case 'in_progress':
        return '#eab308'; // Yellow
      case 'completed':
        return '#22c55e'; // Green
      case 'cancelled':
        return '#fee2e2'; // Pale red (red-100)
      default:
        return '#3b82f6'; // Default blue
    }
  };

  // Add loading and error states to UI
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading bookings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!selectedBusiness) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Please select a business to view bookings.</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col pb-32 h-full overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Bookings</h1>
          {totalCount > 0 && (
            <p className="text-sm text-gray-500">
              Showing {bookings.length} of {totalCount} bookings
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Removed New Booking button as requested */}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-[#e1e3e5] min-h-[400px]">
        <div className="px-4 py-3 border-b border-[#e1e3e5] flex flex-col sm:flex-row gap-3 sm:gap-0 justify-between items-stretch sm:items-center">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <button
                onClick={() => setIsStatusOpen(!isStatusOpen)}
                className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700 focus:outline-none"
              >
                {selectedStatus.label}
                <ChevronDown
                  className={`ml-1.5 h-4 w-4 transition-transform duration-200 ${
                    isStatusOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isStatusOpen && (
                <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedStatus(option);
                          setIsStatusOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm ${
                          selectedStatus.value === option.value
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        role="menuitem"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {selectedBookings.size > 0 && (
              <div className="relative" ref={actionsRef}>
                <button
                  onClick={() => setIsActionsOpen(!isActionsOpen)}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100"
                >
                  Actions ({selectedBookings.size})
                  <ChevronDown className={`ml-1.5 h-3.5 w-3.5 transition-transform duration-200 ${isActionsOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isActionsOpen && (
                  <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10">
                    <button
                      onClick={async () => {
                        setBookingsToDelete(Array.from(selectedBookings));
                        setIsDeleteModalOpen(true);
                        setIsActionsOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center disabled:opacity-50"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-2" />
                      {isDeleting ? 'Deleting...' : 'Delete Selected'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 w-full sm:w-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by booking #, customer name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-64 pl-9 pr-4 py-1.5 text-xs bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Mobile Booking List */}
        <div className="sm:hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-4 text-sm text-gray-600">Loading bookings...</p>
              </div>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <EmptyState
                icon={<Calendar className="w-8 h-8 text-gray-400" />}
                title={searchQuery ? 'No matching bookings' : 'No bookings yet'}
                description={
                  searchQuery
                    ? 'Try adjusting your search terms or filters'
                    : 'Start adding bookings to manage your schedule and track services.'
                }
                actionLabel={
                  searchQuery ? 'Clear search' : 'Create Booking'
                }
                onAction={() => (searchQuery ? setSearchQuery('') : setIsCreateBookingModalOpen(true))}
              />
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredBookings.map((booking) => (
                <div key={booking.id}>
                  <div 
                    className="p-4 hover:bg-gray-50"
                    onClick={(e) => {
                      if (permissionLevel === 'manage' || permissionLevel === 'view') {
                        setSelectedBooking(booking);
                        setIsBookingInfoModalOpen(true);
                      }
                    }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center text-xs font-medium text-white">
                        {booking.customer?.first_name?.[0]}{booking.customer?.last_name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {booking.customer?.first_name} {booking.customer?.last_name}
                          </div>
                          <span 
                            className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(booking.status)}`}
                            style={{ backgroundColor: getStatusBackgroundColor(booking.status) }}
                          >
                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">#{booking.booking_number}</div>
                        <div className="mt-2 text-xs text-gray-500">
                          {booking.services?.map((service, index) => (
                            <div key={index} className="truncate">
                              {service.name}
                              <span className="text-gray-400"> • </span>
                              {service.vehicle_make} {service.vehicle_model}
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="text-xs text-gray-500">
                            {booking.start_date && `${booking.start_date} ${booking.start_time}`}
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            ${booking.total_amount?.toLocaleString() || '0.00'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden sm:block">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-4 text-sm text-gray-600">Loading bookings...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm max-w-md mb-2">
                {error}
              </div>
              {deleteError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm max-w-md mt-2">
                  {deleteError}
                </div>
              )}
              <button
                onClick={() => refetch()}
                className="mt-4 text-sm text-blue-600 hover:text-blue-800"
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {!loading && filteredBookings.length === 0 && (
          <div className="flex items-center justify-center min-h-[400px]">
            <EmptyState
              icon={<Calendar className="w-8 h-8 text-gray-400" />}
              title={searchQuery ? 'No matching bookings' : 'No bookings yet'}
              description={
                searchQuery
                  ? 'Try adjusting your search terms or filters'
                  : 'Start adding bookings to manage your schedule and track services.'
              }
              actionLabel={
                searchQuery ? 'Clear search' : 'Create Booking'
              }
              onAction={() => (searchQuery ? setSearchQuery('') : setIsCreateBookingModalOpen(true))}
            />
          </div>
        )}

        {!loading && filteredBookings.length > 0 && (
          <table className="min-w-full divide-y divide-[#e1e3e5]">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={selectedBookings.size === bookings.length}
                      onChange={toggleAll}
                    />
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking #
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Services
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#e1e3e5]">
              {filteredBookings?.map((booking) => (
                <React.Fragment key={booking.id}>
                  <tr
                    className="hover:bg-[#f6f6f7] transition-colors cursor-pointer"
                    onClick={(e) => {
                      if (permissionLevel === 'manage' || permissionLevel === 'view') {
                        if (e.target instanceof HTMLInputElement && e.target.type === 'checkbox') {
                          return;
                        }
                        setSelectedBooking(booking);
                        setIsBookingInfoModalOpen(true);
                      }
                    }}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        disabled={permissionLevel === 'view'}
                        className="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={selectedBookings.has(booking.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleBooking(booking.id);
                        }}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center text-xs font-medium text-white">
                          {booking.customer?.first_name?.[0]}
                          {booking.customer?.last_name?.[0]}
                        </div>
                        <div className="ml-3">
                          <div className="text-xs font-medium text-gray-900">
                            {booking.customer?.first_name}{' '}
                            {booking.customer?.last_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {booking.customer?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                      #{booking.booking_number}
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {booking.services?.map((service, index) => (
                          <div key={`${service.id}-${index}`} className="text-xs">
                            <span className="text-gray-900">
                              {service.name}
                            </span>
                            <span className="text-gray-500 ml-1">
                              ({service.vehicle_make} {service.vehicle_model})
                            </span>
                            {index < (booking.services?.length || 0) - 1 && <br />}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {booking.start_date &&
                        `${booking.start_date} ${booking.start_time}`}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusColor(
                          booking.status
                        )}`}
                        style={{ backgroundColor: getStatusBackgroundColor(booking.status) }}
                      >
                        {booking.status.charAt(0).toUpperCase() +
                          booking.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                      ${booking.total_amount?.toLocaleString() || '0.00'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button 
                          disabled={permissionLevel === 'view'}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-50"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Phone className="h-3.5 w-3.5" />
                        </button>
                        <button 
                          disabled={permissionLevel === 'view'}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-50"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                        </button>
                        <div className="flex items-center space-x-1">
                          <button
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`/bookings/${booking.id}`, '_blank');
                            }}
                            title="View booking details"
                          >
                            <FileText className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
        </div>


      </div>

      <CreateBookingModal
        isOpen={isCreateBookingModalOpen}
        onClose={() => {
          clearModalData(); // Clear modal persistence data
          setIsCreateBookingModalOpen(false);
        }}
        onSuccess={() => {
          clearModalData(); // Clear modal persistence data
          setIsCreateBookingModalOpen(false);
          refetch();
        }}
      />

      <BookingInfoModal
        isOpen={isBookingInfoModalOpen}
        onClose={() => {
          setIsBookingInfoModalOpen(false);
          setSelectedBooking(null);
          refetch();
        }}
        booking={selectedBooking}
        onUpdate={refetch}
        readOnly={permissionLevel === 'view'}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setBookingsToDelete([]);
        }}
        onConfirm={async () => {
          try {
            setDeleteLoading(true);
            setDeleteError(null);
            
            // 🎯 For each booking to delete, fetch the full booking data and trigger automations BEFORE deleting
            if (selectedBusiness?.id) {
              for (const bookingId of bookingsToDelete) {
                try {
                  console.log(`[BULK DELETE] Processing booking ${bookingId}...`);
                  
                  // Find the full booking data from our current bookings list
                  const bookingToDelete = bookings.find(b => b.id === bookingId);
                  
                  if (bookingToDelete) {
                    console.log(`[BULK DELETE] 🚀 Triggering NEW QStash automations for deleted booking ${bookingToDelete.booking_number}`);
                    
                    // Prepare booking data for automation trigger
                    const realBookingData = {
                      booking_id: bookingToDelete.id,
                      booking_number: bookingToDelete.booking_number,
                      title: bookingToDelete.title,
                      customer: {
                        full_name: `${bookingToDelete.customer?.first_name || ''} ${bookingToDelete.customer?.last_name || ''}`.trim(),
                        phone: bookingToDelete.customer?.phone || undefined,
                        email: bookingToDelete.customer?.email || undefined
                      },
                      schedule: {
                        drop_off_date: bookingToDelete.drop_off_date || '',
                        drop_off_time: bookingToDelete.drop_off_time || '',
                        start_date: bookingToDelete.start_date || '',
                        start_time: bookingToDelete.start_time || '',
                        end_date: bookingToDelete.end_date || '',
                        end_time: bookingToDelete.end_time || ''
                      },
                      services: (bookingToDelete.services || []).map(service => ({
                        name: service.name,
                        price: service.price,
                        duration_minutes: service.duration_minutes || 60
                      })),
                      grand_total: (bookingToDelete as any).grand_total || bookingToDelete.subtotal || 0,
                      status: 'deleted',
                      resources: (bookingToDelete.resources || []).map(resource => ({
                        name: `${(resource as any).first_name || ''} ${(resource as any).last_name || ''}`.trim(),
                        email: (resource as any).email || '',
                        phone: (resource as any).phone || ''
                      })),
                      source: 'bulk_delete_action'
                    };

                    console.log(`🎯 BULK DELETE: Triggering QStash automations for booking ${bookingToDelete.booking_number}:`, realBookingData);
                    await triggerBookingAutomations(selectedBusiness.id, 'deleted', realBookingData);
                    console.log(`🎯 BULK DELETE: QStash automations triggered successfully for booking ${bookingToDelete.booking_number}`);
                  }
                } catch (automationError) {
                  console.error(`❌ BULK DELETE: Failed to trigger QStash automations for booking ${bookingId}:`, automationError);
                  // Don't fail the entire deletion process if automation fails for one booking
                }
              }
            }
            
            // Now delete all the bookings
            await Promise.all(
              bookingsToDelete.map(id => deleteBooking(id))
            );
            setSelectedBookings(new Set());
            await refetch();
            setIsDeleteModalOpen(false);
            setBookingsToDelete([]);
          } catch (err) {
            console.error('Error deleting bookings:', err);
            setDeleteError(err instanceof Error ? err.message : 'Failed to delete bookings');
          } finally {
            setDeleteLoading(false);
          }
        }}
        title={`Delete ${bookingsToDelete.length > 1 ? 'Bookings' : 'Booking'}`}
        message={`Are you sure you want to delete ${bookingsToDelete.length} booking${bookingsToDelete.length > 1 ? 's' : ''}? This action cannot be undone.`}
        loading={deleteLoading}
      />
    </div>
  );
}

// Wrap with PermissionGuard
function BookingsWithPermission() {
  return (
    <PermissionGuard permission="bookings.pageAccess">
      <Bookings />
    </PermissionGuard>
  );
}

export default BookingsWithPermission;